import React, { useId } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import { HiAdjustmentsHorizontal } from "react-icons/hi2";

/**
 * Shared controls for the Sessions / Exercises / Performances lists, mirroring
 * the native list pages: Peak select, borderless filter toggle, circle "+"
 * header button and grayscale pagination.
 */

/** Row text sits on the same 16px inset as text inside the search field and selects. */
export const LIST_ROW_CLASS =
  "px-4 py-3.5 border-b border-peak-border bg-base-100";

export interface PeakSelectOption {
  value: string;
  label: string;
}

/**
 * Peak Design System "Dropdown" (iOS `PeakDropdown`): 60px soft-fill box,
 * floating Funnel Medium 11 label, value in Funnel 15 and a chevron. A native
 * `<select>` sits on top so picking uses the platform list.
 */
export const PeakSelect: React.FC<{
  label: string;
  value: string;
  options: PeakSelectOption[];
  onChange: (value: string) => void;
  /** Sets the chosen value in semibold (list filters / type selects). */
  emphasizedValue?: boolean;
  className?: string;
}> = ({ label, value, options, onChange, emphasizedValue = false, className = "" }) => {
  const id = useId();
  const display = options.find((o) => o.value === value)?.label ?? options[0]?.label ?? "";
  return (
    <div
      className={`relative flex min-h-[60px] w-full items-center gap-2 rounded-lg bg-peak-bg-secondary px-4 py-2 focus-within:ring-[1.5px] focus-within:ring-peak-text ${className}`}
    >
      <div className="min-w-0 flex-1">
        <label htmlFor={id} className="block font-funnel text-[11px] font-medium leading-[18px] text-peak-text-tertiary">
          {label}
        </label>
        <div
          className={`truncate font-funnel text-[15px] leading-6 text-peak-text ${emphasizedValue ? "font-semibold" : "font-normal"}`}
        >
          {display}
        </div>
      </div>
      <svg className="h-5 w-5 flex-shrink-0 text-peak-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
};

/** Borderless slider icon that shows / hides a list's filter selects. */
export const ListFilterToggle: React.FC<{
  open: boolean;
  active: boolean;
  onToggle: () => void;
}> = ({ open, active, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={open ? "Hide filters" : "Show filters"}
    aria-expanded={open}
    className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
      active ? "text-peak-text" : "text-peak-text-tertiary"
    }`}
  >
    <HiAdjustmentsHorizontal className="h-5 w-5" />
  </button>
);

/** Compact circular "+" for a list page's header add action. */
export const ListAddButton = React.forwardRef<
  HTMLButtonElement,
  { label: string; onClick: () => void }
>(({ label, onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    onClick={onClick}
    aria-label={label}
    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full"
  >
    <span className="flex h-[30px] w-[30px] items-center justify-center rounded-full bg-peak-text text-peak-bg">
      <PlusIcon className="h-4 w-4 stroke-[2.5]" />
    </span>
  </button>
));
ListAddButton.displayName = "ListAddButton";

/** "Previous · Page X of Y · Next", grayscale. Renders nothing for one page. */
export const ListPagination: React.FC<{
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}> = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;
  const button =
    "px-2 py-2 font-funnel text-[15px] text-peak-text disabled:text-peak-text-tertiary disabled:cursor-not-allowed";
  return (
    <div className="flex items-center justify-between px-2 py-4">
      <button type="button" className={button} disabled={currentPage <= 1} onClick={() => onPageChange(currentPage - 1)}>
        Previous
      </button>
      <p className="font-funnel text-xs text-peak-text-secondary">
        Page {currentPage} of {totalPages}
      </p>
      <button
        type="button"
        className={button}
        disabled={currentPage >= totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        Next
      </button>
    </div>
  );
};
